import { Alert, AlertDescription, AlertTitle, type AlertVariant } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { AlertCircleIcon, X } from 'lucide-react'
import * as React from 'react'
import { usePathHelper, type AppLinkProps } from '@/hooks/usePathHelper'

export type InfoNoticeTone =
  | 'info'
  | 'global'
  | 'pricing'
  | 'neutral'
  | 'warning'
  | 'danger'
  | 'success'

type NoticeToneClassNames = {
  variant: AlertVariant
  icon: string
}

const noticeToneClassNames: Record<InfoNoticeTone, NoticeToneClassNames> = {
  info: {
    variant: 'info',
    icon: 'border-status-info-border bg-card/55 text-status-info-foreground dark:bg-card/35',
  },
  global: {
    variant: 'info',
    icon: 'border-status-info-border bg-card/55 text-status-info-foreground dark:bg-card/35',
  },
  pricing: {
    variant: 'info',
    icon: 'border-status-info-border bg-card/55 text-status-info-foreground dark:bg-card/35',
  },
  neutral: {
    variant: 'neutral',
    icon: 'border-border/30 bg-muted/45 text-muted-foreground dark:border-white/10 dark:bg-white/[0.06]',
  },
  warning: {
    variant: 'warning',
    icon: 'border-status-warning-border bg-card/55 text-status-warning-foreground dark:bg-card/35',
  },
  danger: {
    variant: 'destructive',
    icon: 'border-status-destructive-border bg-card/55 text-status-destructive-foreground dark:bg-card/35',
  },
  success: {
    variant: 'success',
    icon: 'border-status-success-border bg-card/55 text-status-success-foreground dark:bg-card/35',
  },
}

export type InfoNoticeActionProps = AppLinkProps & {
  children?: React.ReactNode
  className?: string
}

export function InfoNoticeAction({
  children,
  className = '',
  ...linkProps
}: InfoNoticeActionProps) {
  return (
    <Button asChild variant="notice" size="xs" className={className}>
      <Link {...linkProps}>{children}</Link>
    </Button>
  )
}

export type InfoNoticeProps = {
  title?: React.ReactNode
  children?: React.ReactNode
  body?: React.ReactNode
  action?: React.ReactNode
  icon?: React.ReactNode
  tone?: InfoNoticeTone
  onDismiss?: React.MouseEventHandler<HTMLButtonElement> | null
  dismissLabel?: string
  compact?: boolean
  className?: string
}

type NoticeIconProps = {
  className?: string
  'aria-hidden'?: React.AriaAttributes['aria-hidden']
}

export function InfoNotice({
  title = null,
  children,
  body = null,
  action = null,
  icon = <AlertCircleIcon />,
  tone = 'info',
  onDismiss = null,
  dismissLabel = 'Dismiss notice',
  compact = false,
  className = '',
}: InfoNoticeProps) {
  const toneClassNames = noticeToneClassNames[tone] ?? noticeToneClassNames.info
  const renderedIcon = React.isValidElement<NoticeIconProps>(icon)
    ? React.cloneElement(icon, {
        className: cn('relative shrink-0', compact ? 'size-4' : 'size-5', icon.props.className),
        'aria-hidden': icon.props['aria-hidden'] ?? true,
      })
    : icon

  return (
    <Alert
      variant={toneClassNames.variant}
      layout="notice"
      data-density={compact ? 'compact' : 'default'}
      data-tone={tone}
      className={cn('@container/info-notice', className)}
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-[inherit] border border-transparent',
          compact ? 'px-3 py-2.5' : 'px-4 py-4 sm:px-5'
        )}
      >
        {onDismiss ? (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-10 size-8"
            aria-label={dismissLabel}
            onClick={onDismiss}
          >
            <X className="size-4" />
          </Button>
        ) : null}
        <div
          className={cn(
            'relative flex gap-3',
            compact
              ? 'flex-col items-start @lg/info-notice:flex-row'
              : 'flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'
          )}
        >
          <div
            className={cn('flex min-w-0 flex-1 gap-3', compact ? 'items-start' : 'items-center')}
          >
            <div
              className={cn(
                'relative flex shrink-0 items-center justify-center rounded-md border',
                toneClassNames.icon,
                compact ? 'size-8 rounded-md' : 'size-11'
              )}
            >
              {renderedIcon}
            </div>

            <div className="min-w-0 space-y-1.5">
              {title ? <AlertTitle className={cn(onDismiss && 'pr-10')}>{title}</AlertTitle> : null}

              {children ? (
                <AlertDescription className={cn('gap-2', onDismiss && !title && 'pr-10')}>
                  {children}
                </AlertDescription>
              ) : null}
            </div>
          </div>

          {action ? (
            <div
              className={cn(
                'relative flex shrink-0 items-start gap-2 self-end',
                onDismiss && 'sm:mr-10'
              )}
            >
              {action}
            </div>
          ) : null}
        </div>

        {body ? <div className={cn('relative', compact ? 'pt-2.5' : 'pt-4')}>{body}</div> : null}
      </div>
    </Alert>
  )
}

export type OrganizationContextNoticeProps = {
  title?: React.ReactNode
  children?: React.ReactNode
  className?: string
  actionLabel?: React.ReactNode
  tone?: InfoNoticeTone
  onDismiss?: InfoNoticeProps['onDismiss']
  dismissLabel?: string
}

export function OrganizationContextNotice({
  title = 'Global Users!',
  children,
  className = '',
  actionLabel = 'Impersonate an organization',
  tone = 'global',
  onDismiss = null,
  dismissLabel = 'Dismiss global organization notice',
}: OrganizationContextNoticeProps) {
  const path = usePathHelper()

  return (
    <InfoNotice
      title={title}
      tone={tone}
      className={className}
      onDismiss={onDismiss}
      dismissLabel={dismissLabel}
      action={
        actionLabel ? (
          <InfoNoticeAction {...path.organizations.index().link}>{actionLabel}</InfoNoticeAction>
        ) : null
      }
    >
      {children}
    </InfoNotice>
  )
}
